import { Fragment } from 'react'
import { Check } from 'lucide-react'
import * as Radix from '@radix-ui/react-dropdown-menu'
import { cn } from '../../lib/cn.js'
import { usePortalTarget } from './PortalTarget.js'

export interface MenuItem {
  label: React.ReactNode
  onSelect: () => void
  icon?: React.ReactNode
  /** The error ink, and always last with a line above it. */
  destructive?: boolean
  disabled?: boolean
  /**
   * One of a set, and this is the one in force.
   *
   * Draws a tick and tells a screen reader it is a choice rather than an
   * action — `menuitemradio`, because "Light / Dark / System" is three
   * readings of one setting, not three things to do.
   */
  selected?: boolean
  /** A quiet heading above the items that follow. Not selectable. */
  section?: string
  /**
   * A control rather than an action — a segmented group, say.
   *
   * Rendered outside `Radix.Item`, because an item closes the menu when it
   * is chosen and swallows the arrow keys the control wants. `label` and
   * `onSelect` are ignored when this is set.
   */
  node?: React.ReactNode
}

/**
 * The actions on a row that do not deserve a button each.
 *
 * A destructive item is separated and drawn in the error ink, because the
 * distance between "open" and "remove" in a menu is one keystroke.
 */
export function DropdownMenu({
  trigger,
  items,
  align = 'end',
}: {
  trigger: React.ReactNode
  items: MenuItem[]
  align?: 'start' | 'end'
}) {
  const safe = items.filter((i) => !i.destructive)
  const destructive = items.filter((i) => i.destructive)
  const render = (item: MenuItem, i: number) => (
    <Fragment key={i}>
      {item.section && (
        <Radix.Label className="text-micro text-ink-muted px-3 pb-1 pt-2 font-medium uppercase tracking-wide">
          {item.section}
        </Radix.Label>
      )}
      {item.node ? (
        /* Outside the item list on purpose: `onKeyDown` stops the menu from
           reading the arrow keys the control inside needs, and nothing here
           closes the menu, so the choice can be changed and compared without
           reopening it three times. */
        <div className="px-2 py-1" onKeyDown={(e) => e.stopPropagation()}>
          {item.node}
        </div>
      ) : (
        <Radix.Item
          disabled={item.disabled}
          onSelect={item.onSelect}
          {...(item.selected !== undefined
            ? { role: 'menuitemradio', 'aria-checked': item.selected }
            : {})}
          className={cn(
            'flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-body outline-none',
            'data-[highlighted]:bg-sunk data-[disabled]:opacity-50 data-[disabled]:pointer-events-none',
            '[&_svg]:size-4 [&_svg]:shrink-0',
            item.destructive ? 'text-error' : 'text-ink',
          )}
        >
          {item.icon}
          <span className="flex-1">{item.label}</span>
          {/* The tick keeps its space when unticked, so the labels line up
            rather than shuffling as the choice moves. */}
          {item.selected !== undefined && (
            <Check
              className={cn('text-coral-text', item.selected ? '' : 'invisible')}
              aria-hidden
            />
          )}
        </Radix.Item>
      )}
    </Fragment>
  )
  /* See `PortalTarget`: a menu portalled to the body from inside a dialog
     cannot be scrolled. */
  const container = usePortalTarget()
  return (
    <Radix.Root>
      <Radix.Trigger asChild>{trigger}</Radix.Trigger>
      <Radix.Portal {...(container ? { container } : {})}>
        <Radix.Content
          align={align}
          sideOffset={6}
          className="z-50 min-w-44 rounded-md border border-hairline bg-surface p-1 shadow-lg"
        >
          {safe.map(render)}
          {destructive.length > 0 && safe.length > 0 && (
            <Radix.Separator className="my-1 h-px bg-hairline" />
          )}
          {destructive.map((item, i) => render(item, safe.length + i))}
        </Radix.Content>
      </Radix.Portal>
    </Radix.Root>
  )
}
